import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useIonAlert, useIonLoading } from "@ionic/react";
import { collectionGroup, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

export const WERKER_JOB_TAP_STATUSES = new Set(["Invited", "Scheduled", "Past"]);

export function isWerkerJobTapStatusAllowed(jobStatus?: string | null) {
  return WERKER_JOB_TAP_STATUSES.has(String(jobStatus ?? ""));
}

/**
 * Series metadata (`seriesId`, `seriesIndex`) lives only on the scheduler's
 * self-doc for each occurrence — i.e. the doc where both userInfoFrom.uid
 * and userInfoTo.uid equal the scheduler's UID. Use this matcher when walking
 * a series via collectionGroup queries.
 */
export function isSchedulerSelfDocForJob(docSnap: any, schedUid: string) {
  return (
    docSnap?.data?.()?.docStamp?.userInfoFrom?.uid === schedUid &&
    docSnap?.data?.()?.docStamp?.userInfoTo?.uid === schedUid
  );
}

async function resolveWerkerJobDocId(uid: string, uniqueJobDocId: string) {
  const feedSnap = await getDoc(doc(db, "users", uid, "jobFeed", uniqueJobDocId));
  const feedData = feedSnap.data?.() as { werkerJobDocId?: string } | undefined;
  if (feedSnap.exists() && feedData?.werkerJobDocId) {
    return feedData.werkerJobDocId;
  }

  const directSnap = await getDocs(query(
    collectionGroup(db, "jobsDocs"),
    where("docStamp.userInfoTo.uid", "==", uid),
    where("jobInfo.core.uniqueJobId", "==", uniqueJobDocId)
  ));

  const directDoc = directSnap.docs.find((d) => isWerkerJobTapStatusAllowed(d.data()?.jobInfo?.status?.jobStatusWerker));
  return directDoc?.id ?? null;
}

/**
 * Shared notification tap handlers used by both the in-app Notifications page
 * and the push notification deep link router in Home.tsx.
 *
 * @param uid Current user's UID (required for werker-specific queries)
 */
export function useNotificationTapHandlers(uid: string) {
  const history = useHistory();
  const [present] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();

  // Legacy fallback: navigate to Home if the werker's job doc exists
  const navigateToWerkerShift = useCallback(
    async (uniqueJobDocId: string, _schedUid: string) => {
      try {
        const snap = await getDocs(query(
          collectionGroup(db, "jobsDocs"),
          where("docStamp.userInfoTo.uid", "==", uid),
          where("jobInfo.core.uniqueJobId", "==", uniqueJobDocId)
        ));
        if (!snap.empty) {
          history.push("/Home");
        }
      } catch (err) {
        // no-op
      }
    },
    [uid, history]
  );

  // Types: WerkerJobAvailable, WerkerJobReminder
  const handleWerkerJobTap = useCallback(
    async (uniqueJobDocId: string, schedUid: string) => {
      await presentLoading({ message: "Opening job...", duration: 10000 });
      try {
        // The notification's `uniqueJobDocId` field can be either:
        //   (a) the werker's jobsDocs document id (current cloud-function payload), or
        //   (b) the canonical jobInfo.core.uniqueJobId (older / direct call sites).
        // Try (a) first via a single getDoc, then fall back to (b) via the user's job feed
        // and a collectionGroup lookup.
        const directSnap = await getDoc(doc(db, "jobsDocs", uniqueJobDocId));
        if (directSnap.exists()) {
          const data: any = directSnap.data();
          if (data?.docStamp?.userInfoTo?.uid === uid) {
            const jobStatus = data?.jobInfo?.status?.jobStatusWerker;
            const canonicalJobId: string = data?.jobInfo?.core?.uniqueJobId ?? uniqueJobDocId;
            if (isWerkerJobTapStatusAllowed(jobStatus)) {
              await dismissLoading();
              history.push(`/ShiftDetails/${directSnap.id}/${canonicalJobId}/${schedUid}`);
              return;
            }
            await dismissLoading();
            present({ header: "Job Unavailable", message: "This job has been taken.", buttons: ["OK"] });
            return;
          }
        }

        const resolvedWerkerDocId = await resolveWerkerJobDocId(uid, uniqueJobDocId);

        if (resolvedWerkerDocId) {
          const werkerDoc = await getDoc(doc(db, "jobsDocs", resolvedWerkerDocId));
          const jobStatus = werkerDoc.data()?.jobInfo?.status?.jobStatusWerker;
          if (isWerkerJobTapStatusAllowed(jobStatus)) {
            await dismissLoading();
            history.push(`/ShiftDetails/${resolvedWerkerDocId}/${uniqueJobDocId}/${schedUid}`);
            return;
          }
        }

        // Step 2: Job not available — check if this is a series.
        const jobByIdSnap = await getDocs(query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", uniqueJobDocId)
        ));
        const schedDoc = jobByIdSnap.docs.find(
          (d) => isSchedulerSelfDocForJob(d, schedUid)
        );
        const seriesId: string | null = schedDoc?.data()?.jobInfo?.core?.seriesId ?? null;

        if (!seriesId) {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job has been taken.", buttons: ["OK"] });
          return;
        }

        // Step 3: Walk series occurrences in order, navigate to first still-available job.
        const seriesByIdSnap = await getDocs(query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.seriesId", "==", seriesId)
        ));
        const schedSeriesDocs = seriesByIdSnap.docs
          .filter((d) => isSchedulerSelfDocForJob(d, schedUid))
          .sort(
            (a, b) =>
              (a.data()?.jobInfo?.core?.seriesIndex ?? 0) -
              (b.data()?.jobInfo?.core?.seriesIndex ?? 0)
          );

        for (const seriesDoc of schedSeriesDocs) {
          const occurrenceJobId: string = seriesDoc.data()?.jobInfo?.core?.uniqueJobId;
          if (!occurrenceJobId) continue;

          const occurrenceSnap = await getDocs(query(
            collectionGroup(db, "jobsDocs"),
            where("docStamp.userInfoTo.uid", "==", uid),
            where("jobInfo.core.uniqueJobId", "==", occurrenceJobId)
          ));

          if (!occurrenceSnap.empty) {
            const occurrenceDoc = occurrenceSnap.docs[0];
            const occurrenceStatus = occurrenceDoc.data()?.jobInfo?.status?.jobStatusWerker;
            if (isWerkerJobTapStatusAllowed(occurrenceStatus)) {
              await dismissLoading();
              history.push(`/ShiftDetails/${occurrenceDoc.id}/${occurrenceJobId}/${schedUid}`);
              return;
            }
          }
        }

        await dismissLoading();
        present({
          header: "Jobs Unavailable",
          message: "The jobs in this series have all been taken.",
          buttons: ["OK"],
        });
      } catch (err) {
        await dismissLoading();
        present({ header: "Job Unavailable", message: "This job has been taken.", buttons: ["OK"] });
      }
    },
    [uid, history, present, presentLoading, dismissLoading]
  );

  // Type: SchedulerAddedWerkerToShift
  const handleSchedulerAddedTap = useCallback(
    async (werkerJobDocId: string, uniqueJobDocId: string, schedUid: string) => {
      await presentLoading({ message: "Opening job...", duration: 10000 });
      try {
        const werkerDocSnap = await getDoc(doc(db, "jobsDocs", werkerJobDocId));

        if (!werkerDocSnap.exists()) {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job was deleted by the Scheduler.", buttons: ["OK"] });
          return;
        }

        const jobStatus = werkerDocSnap.data()?.jobInfo?.status?.jobStatusWerker;

        if (
          jobStatus === "Scheduled" ||
          jobStatus === "Past" ||
          jobStatus === "Invited" ||
          jobStatus === "Cancelled"
        ) {
          await dismissLoading();
          history.push(`/ShiftDetails/${werkerJobDocId}/${uniqueJobDocId}/${schedUid}`);
        } else if (jobStatus === "Deleted") {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job was deleted by the Scheduler.", buttons: ["OK"] });
        } else {
          await dismissLoading();
          present({ header: "Removed from Job", message: "You have been removed from this job.", buttons: ["OK"] });
        }
      } catch (err) {
        await dismissLoading();
        present({ header: "Job Unavailable", message: "This job was deleted by the Scheduler.", buttons: ["OK"] });
      }
    },
    [history, present, presentLoading, dismissLoading]
  );

  // Type: SchedulerEditedJob
  const handleSchedulerEditedJobTap = useCallback(
    async (werkerJobDocId: string, uniqueJobDocId: string) => {
      await presentLoading({ message: "Opening job...", duration: 10000 });
      try {
        const werkerDocSnap = await getDoc(doc(db, "jobsDocs", werkerJobDocId));

        if (!werkerDocSnap.exists()) {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
          return;
        }

        const jobStatus = werkerDocSnap.data()?.jobInfo?.status?.jobStatusWerker;
        const schedUid = werkerDocSnap.data()?.docStamp?.userInfoFrom?.uid;

        if ((jobStatus === "Invited" || jobStatus === "Scheduled") && schedUid) {
          await dismissLoading();
          history.push(`/ShiftDetails/${werkerJobDocId}/${uniqueJobDocId}/${schedUid}`);
        } else {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
        }
      } catch (err) {
        await dismissLoading();
        present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
      }
    },
    [history, present, presentLoading, dismissLoading]
  );

  // Type: SchedulerRemovedWerkerFromShift
  const handleSchedulerRemovedTap = useCallback(
    async (werkerJobDocId: string, uniqueJobDocId: string) => {
      await presentLoading({ message: "Opening job...", duration: 10000 });
      try {
        const werkerDocSnap = await getDoc(doc(db, "jobsDocs", werkerJobDocId));

        if (!werkerDocSnap.exists()) {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
          return;
        }

        const jobStatus = werkerDocSnap.data()?.jobInfo?.status?.jobStatusWerker;
        const schedUid = werkerDocSnap.data()?.docStamp?.userInfoFrom?.uid;

        if ((jobStatus === "Invited" || jobStatus === "Scheduled") && schedUid) {
          await dismissLoading();
          history.push(`/ShiftDetails/${werkerJobDocId}/${uniqueJobDocId}/${schedUid}`);
        } else {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
        }
      } catch (err) {
        await dismissLoading();
        present({ header: "Job Unavailable", message: "This job is no longer available.", buttons: ["OK"] });
      }
    },
    [history, present, presentLoading, dismissLoading]
  );

  // Types: SchedulerFilledJobs, SchedulerUnFilledJobs
  const handleSchedulerFilledJobTap = useCallback(
    async (uniqueJobDocId: string) => {
      await presentLoading({ message: "Opening job...", duration: 10000 });
      try {
        const snap = await getDocs(query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", uniqueJobDocId)
        ));
        const schedDoc = snap.docs.find(
          (d) =>
            d.data()?.docStamp?.userInfoFrom?.uid === uid &&
            d.data()?.docStamp?.userInfoTo?.uid === uid
        );
        const jobStatusSched = schedDoc?.data()?.jobInfo?.status?.jobStatusSched;
        if (schedDoc && jobStatusSched !== "Deleted") {
          await dismissLoading();
          history.push(`/SchedShiftDetail/${uniqueJobDocId}`);
        } else {
          await dismissLoading();
          present({ header: "Job Unavailable", message: "This job no longer exists.", buttons: ["OK"] });
        }
      } catch (err) {
        await dismissLoading();
        present({ header: "Job Unavailable", message: "This job no longer exists.", buttons: ["OK"] });
      }
    },
    [uid, history, present, presentLoading, dismissLoading]
  );

  return {
    navigateToWerkerShift,
    handleWerkerJobTap,
    handleSchedulerAddedTap,
    handleSchedulerEditedJobTap,
    handleSchedulerRemovedTap,
    handleSchedulerFilledJobTap,
  };
}
